import { useEffect, useRef, useState } from 'react';
import type { Attachment, Note, NoteGroup } from '../../types';
import Modal from '@/components/base/Modal';
import { supabase } from '@/lib/supabase';
import { toDatetimeLocal } from './noteUtils';
import { extractLinks, hasLink } from '../LinkifiedText';
import { fileIcon, formatFileSize } from '../attachmentUtils';

export interface NotePayload {
  title: string;
  content: string;
  groupId: string | null;
  expiresAt: string | null;
  telegramAlert: boolean;
  imageFiles: File[];
  existingImageUrls: string[];
  attachmentFiles: File[];
  existingAttachments: Attachment[];
}

interface NoteEditorModalProps {
  open: boolean;
  editing: Note | null;
  groups: NoteGroup[];
  defaultGroupId?: string | null;
  saving: boolean;
  error: string;
  onClose: () => void;
  onSave: (payload: NotePayload) => void;
}

const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_ATTACHMENT_SIZE = 50 * 1024 * 1024; // 50MB

export default function NoteEditorModal({
  open,
  editing,
  groups,
  defaultGroupId,
  saving,
  error,
  onClose,
  onSave,
}: NoteEditorModalProps) {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [groupId, setGroupId] = useState<string>('');
  const [hasExpiry, setHasExpiry] = useState(false);
  const [expiresAtLocal, setExpiresAtLocal] = useState('');
  const [telegramAlert, setTelegramAlert] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [keptImages, setKeptImages] = useState<string[]>([]);
  const [keptPreviewMap, setKeptPreviewMap] = useState<Record<string, string>>(
    {}
  );
  const [imageError, setImageError] = useState('');

  const attachmentInputRef = useRef<HTMLInputElement>(null);
  const [attachmentFiles, setAttachmentFiles] = useState<File[]>([]);
  const [keptAttachments, setKeptAttachments] = useState<Attachment[]>([]);
  const [attachmentError, setAttachmentError] = useState('');

  useEffect(() => {
    if (open) {
      setTitle(editing?.title ?? '');
      setContent(editing?.content ?? '');
      setGroupId(editing?.group_id ?? defaultGroupId ?? '');
      setHasExpiry(!!editing?.expires_at);
      setExpiresAtLocal(
        editing?.expires_at ? toDatetimeLocal(editing.expires_at) : ''
      );
      setTelegramAlert(editing?.telegram_alert ?? false);

      // Reset trạng thái ảnh
      setImageFiles([]);
      setImagePreviews([]);
      setImageError('');
      setKeptPreviewMap({});

      // Reset trạng thái file đính kèm
      setAttachmentFiles([]);
      setAttachmentError('');
      setKeptAttachments(editing?.attachments ?? []);

      // Giữ lại các ảnh cũ đang có
      const existing = editing?.image_urls ?? [];
      setKeptImages(existing);
      if (existing.length) {
        existing.forEach(async (path) => {
          try {
            const { data } = await supabase.storage
              .from('noteapp_images')
              .createSignedUrl(path, 3600);
            if (data?.signedUrl) {
              setKeptPreviewMap((prev) => ({ ...prev, [path]: data.signedUrl }));
            }
          } catch {
            // Bỏ qua ảnh không tải được
          }
        });
      }
    }
  }, [open, editing, defaultGroupId]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;

    const valid: File[] = [];
    let err = '';
    files.forEach((file) => {
      if (!file.type.startsWith('image/')) {
        err = 'Chỉ hỗ trợ file ảnh (PNG, JPG, WEBP, GIF).';
        return;
      }
      if (file.size > MAX_IMAGE_SIZE) {
        err = 'Mỗi ảnh tối đa 10MB.';
        return;
      }
      valid.push(file);
    });

    if (err) setImageError(err);
    else setImageError('');

    if (valid.length) {
      const newPreviews = valid.map((file) => URL.createObjectURL(file));
      setImageFiles((prev) => [...prev, ...valid]);
      setImagePreviews((prev) => [...prev, ...newPreviews]);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeNewImage = (index: number) => {
    setImageFiles((prev) => prev.filter((_, i) => i !== index));
    setImagePreviews((prev) => {
      const url = prev[index];
      if (url) URL.revokeObjectURL(url);
      return prev.filter((_, i) => i !== index);
    });
  };

  const removeKeptImage = (path: string) => {
    setKeptImages((prev) => prev.filter((p) => p !== path));
    setKeptPreviewMap((prev) => {
      const next = { ...prev };
      delete next[path];
      return next;
    });
  };

  const totalImages = keptImages.length + imageFiles.length;

  const handleAttachmentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;

    const valid: File[] = [];
    let err = '';
    files.forEach((file) => {
      if (file.size > MAX_ATTACHMENT_SIZE) {
        err = 'Mỗi file tối đa 50MB.';
        return;
      }
      valid.push(file);
    });

    if (err) setAttachmentError(err);
    else setAttachmentError('');

    if (valid.length) {
      setAttachmentFiles((prev) => [...prev, ...valid]);
    }
    if (attachmentInputRef.current) attachmentInputRef.current.value = '';
  };

  const removeNewAttachment = (index: number) => {
    setAttachmentFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const removeKeptAttachment = (path: string) => {
    setKeptAttachments((prev) => prev.filter((a) => a.path !== path));
  };

  const totalAttachments = keptAttachments.length + attachmentFiles.length;

  const submit = () => {
    onSave({
      title: title.trim(),
      content,
      groupId: groupId || null,
      expiresAt: hasExpiry && expiresAtLocal ? expiresAtLocal : null,
      telegramAlert: hasExpiry ? telegramAlert : false,
      imageFiles,
      existingImageUrls: keptImages,
      attachmentFiles,
      existingAttachments: keptAttachments,
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? 'Chỉnh sửa ghi chú' : 'Thêm ghi chú'}
      maxWidth="max-w-xl"
    >
      <div className="space-y-4">
        <div>
          <label className="mb-1.5 block text-sm font-medium text-foreground-700">
            Tiêu đề <span className="text-accent-500">*</span>
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Nhập tiêu đề..."
            className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
          />
        </div>

        <div>
          <label className="mb-1.5 block text-sm font-medium text-foreground-700">
            Nhóm ghi chú
          </label>
          <select
            value={groupId}
            onChange={(e) => setGroupId(e.target.value)}
            className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
          >
            <option value="">Chưa phân nhóm</option>
            {groups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="mb-1.5 block text-sm font-medium text-foreground-700">
            Nội dung
          </label>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Nhập nội dung ghi chú..."
            rows={7}
            className="w-full resize-y rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm leading-relaxed text-foreground-900 outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
          />

          {hasLink(content) && (
            <div className="mt-2 rounded-lg border border-background-200 bg-background-100/60 p-3">
              <div className="mb-2 flex items-center gap-1.5 text-xs font-medium text-foreground-500">
                <i className="ri-link-m text-sm text-blue-500" />
                Đã nhận diện liên kết
              </div>
              <div className="space-y-1.5">
                {extractLinks(content).map((url) => (
                  <a
                    key={url}
                    href={url}
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="flex items-start gap-1.5 break-all text-sm text-blue-600 underline decoration-blue-300 underline-offset-2 transition hover:text-blue-700"
                  >
                    <i className="ri-external-link-line mt-0.5 shrink-0 text-sm" />
                    <span>{url}</span>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>

        <div>
          <label className="mb-1.5 block text-sm font-medium text-foreground-700">
            Hình minh họa
          </label>
          {totalImages > 0 ? (
            <div className="grid grid-cols-3 gap-2">
              {keptImages.map((path) => (
                <div
                  key={path}
                  className="group relative aspect-square overflow-hidden rounded-lg border border-background-200"
                >
                  <img
                    src={keptPreviewMap[path]}
                    alt="Hình minh họa"
                    className="h-full w-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => removeKeptImage(path)}
                    className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-md bg-black/50 text-white transition hover:bg-black/70"
                    aria-label="Gỡ ảnh"
                  >
                    <i className="ri-close-line text-sm" />
                  </button>
                </div>
              ))}
              {imagePreviews.map((url, index) => (
                <div
                  key={`new-${index}`}
                  className="group relative aspect-square overflow-hidden rounded-lg border border-background-200"
                >
                  <img
                    src={url}
                    alt="Hình minh họa mới"
                    className="h-full w-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => removeNewImage(index)}
                    className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-md bg-black/50 text-white transition hover:bg-black/70"
                    aria-label="Gỡ ảnh"
                  >
                    <i className="ri-close-line text-sm" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="flex aspect-square items-center justify-center rounded-lg border-2 border-dashed border-background-300 text-foreground-400 transition hover:border-primary-400 hover:text-primary-700"
                aria-label="Thêm ảnh"
              >
                <i className="ri-add-line text-2xl" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex w-full flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-background-300 bg-background-50 px-4 py-6 text-foreground-500 transition hover:border-primary-400 hover:text-primary-700"
            >
              <i className="ri-image-add-line text-2xl" />
              <span className="text-sm font-medium">
                Tải ảnh lên để minh họa
              </span>
              <span className="text-xs text-foreground-400">
                PNG, JPG, WEBP, GIF · tối đa 10MB/ảnh
              </span>
            </button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileChange}
            className="hidden"
          />
          {imageError && (
            <p className="mt-1.5 text-xs text-accent-600">{imageError}</p>
          )}
        </div>

        <div>
          <label className="mb-1.5 block text-sm font-medium text-foreground-700">
            File đính kèm
          </label>
          {totalAttachments > 0 && (
            <div className="mb-2 space-y-1.5">
              {keptAttachments.map((a) => (
                <div
                  key={a.path}
                  className="flex items-center gap-2.5 rounded-lg border border-background-200 bg-background-50 px-3 py-2"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-secondary-100 text-secondary-700">
                    <i className={`${fileIcon(a.name)} text-lg`} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-foreground-900">
                      {a.name}
                    </p>
                    <p className="text-xs text-foreground-400">
                      {formatFileSize(a.size)}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeKeptAttachment(a.path)}
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-foreground-400 transition hover:bg-accent-100 hover:text-accent-700"
                    aria-label={`Gỡ ${a.name}`}
                  >
                    <i className="ri-close-line text-base" />
                  </button>
                </div>
              ))}
              {attachmentFiles.map((file, index) => (
                <div
                  key={`new-att-${index}`}
                  className="flex items-center gap-2.5 rounded-lg border border-background-200 bg-background-50 px-3 py-2"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-secondary-100 text-secondary-700">
                    <i className={`${fileIcon(file.name)} text-lg`} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-foreground-900">
                      {file.name}
                    </p>
                    <p className="text-xs text-foreground-400">
                      {formatFileSize(file.size)}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeNewAttachment(index)}
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-foreground-400 transition hover:bg-accent-100 hover:text-accent-700"
                    aria-label={`Gỡ ${file.name}`}
                  >
                    <i className="ri-close-line text-base" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <button
            type="button"
            onClick={() => attachmentInputRef.current?.click()}
            className="flex w-full flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-background-300 bg-background-50 px-4 py-5 text-foreground-500 transition hover:border-primary-400 hover:text-primary-700"
          >
            <i className="ri-attachment-2 text-2xl" />
            <span className="text-sm font-medium">Thêm file đính kèm</span>
            <span className="text-xs text-foreground-400">
              PDF, Word, Excel, ZIP... · tối đa 50MB/file
            </span>
          </button>
          <input
            ref={attachmentInputRef}
            type="file"
            multiple
            onChange={handleAttachmentChange}
            className="hidden"
          />
          {attachmentError && (
            <p className="mt-1.5 text-xs text-accent-600">{attachmentError}</p>
          )}
        </div>

        <div className="rounded-lg border border-background-200 bg-background-100/60 p-3.5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <i className="ri-calendar-check-line text-base text-foreground-500" />
              <span className="text-sm font-medium text-foreground-700">
                Ngày hết hạn
              </span>
            </div>
            <button
              type="button"
              onClick={() => setHasExpiry((v) => !v)}
              className={`relative h-6 w-11 rounded-full transition ${
                hasExpiry ? 'bg-primary-500' : 'bg-background-300'
              }`}
              aria-label="Bật/tắt ngày hết hạn"
            >
              <span
                className={`absolute top-0.5 h-5 w-5 rounded-full bg-background-50 transition-all ${
                  hasExpiry ? 'left-[22px]' : 'left-0.5'
                }`}
              />
            </button>
          </div>

          {hasExpiry && (
            <input
              type="datetime-local"
              value={expiresAtLocal}
              onChange={(e) => setExpiresAtLocal(e.target.value)}
              className="mt-3 w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
            />
          )}
        </div>

        {hasExpiry && (
          <div className="rounded-lg border border-background-200 bg-background-100/60 p-3.5">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-2">
                <i className="ri-send-plane-2-line mt-0.5 text-base text-secondary-600" />
                <div>
                  <p className="text-sm font-medium text-foreground-700">
                    Cảnh báo Telegram
                  </p>
                  <p className="mt-0.5 text-xs text-foreground-500">
                    Gửi cảnh báo tới Telegram 1 ngày trước khi hết hạn.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setTelegramAlert((v) => !v)}
                className={`relative h-6 w-11 shrink-0 rounded-full transition ${
                  telegramAlert ? 'bg-secondary-500' : 'bg-background-300'
                }`}
                aria-label="Bật/tắt cảnh báo Telegram"
              >
                <span
                  className={`absolute top-0.5 h-5 w-5 rounded-full bg-background-50 transition-all ${
                    telegramAlert ? 'left-[22px]' : 'left-0.5'
                  }`}
                />
              </button>
            </div>
          </div>
        )}

        {error && (
          <div className="flex items-start gap-2 rounded-md bg-accent-100 px-3 py-2.5 text-sm text-accent-900">
            <i className="ri-error-warning-line mt-0.5 text-base" />
            <span>{error}</span>
          </div>
        )}

        <div className="flex items-center justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-700 transition hover:bg-background-100"
          >
            Hủy
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving && (
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-background-50 border-t-transparent" />
            )}
            Lưu
          </button>
        </div>
      </div>
    </Modal>
  );
}